/**
 * Per-network managed vector index backend (API: vectorDbProvider).
 *
 * Selection is frozen on first sync (server enforces vectorDbFrozen). New
 * networks default to S3 Vectors.
 *
 * v1 suppression (USAGE-AND-BUDGET.md §10): only S3 Vectors is *selectable* —
 * LanceDB / dual are not offered to users. We still KEEP them as KNOWN providers
 * here (not deleted) so the UI can truthfully LABEL a network that is already
 * frozen on one of them; we just don't let anyone pick them for a new network.
 * ``normalizeVectorDbProvider`` therefore still recognizes the stored values
 * (it must not silently rewrite a frozen LanceDB network to "S3 Vectors").
 */
export const VECTOR_DB_PROVIDERS = [
  {
    value: 's3vectors',
    label: 'S3 Vectors',
    description:
      'Managed semantic search — low latency, simple operations, predictable cost.',
  },
  {
    value: 'lancedb',
    label: 'LanceDB hybrid',
    description: 'Higher quality (BM25 + vector + reranker). Use when retrieval precision matters most.',
  },
  {
    value: 'dual',
    label: 'Dual write (S3 Vectors + LanceDB)',
    description: 'Write both sinks; reads default to S3 Vectors. Pick this if you want to keep your options open.',
  },
];

export const DEFAULT_VECTOR_DB_PROVIDER = 's3vectors';

/**
 * v1: the only provider a user may newly SELECT. The full ``VECTOR_DB_PROVIDERS``
 * set stays available for labeling/normalization (frozen networks). To re-expose
 * LanceDB/dual later, add them back here.
 */
export const SELECTABLE_VECTOR_DB_PROVIDERS = VECTOR_DB_PROVIDERS.filter(
  (o) => o.value === 's3vectors',
);

export function normalizeVectorDbProvider(raw) {
  const v = (raw || '').trim().toLowerCase();
  const allowed = new Set(VECTOR_DB_PROVIDERS.map((o) => o.value));
  if (allowed.has(v)) return v;
  return DEFAULT_VECTOR_DB_PROVIDER;
}
